import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';

/**
 * Time range preset configuration
 */
export interface TimeRangePreset {
  key: string;
  label: string;
  startTime: Date;
  endTime: Date;
  granularity: Granularity;
}

/**
 * Get a date range for the last N days from a reference date
 * @param days Number of days to go back from the reference date
 * @param referenceDate Optional reference date (defaults to today)
 * @returns Object with startTime and endTime
 */
export function getLastNDays(days: number, referenceDate?: Date): { startTime: Date; endTime: Date } {
  const endTime = referenceDate ? new Date(referenceDate) : new Date();
  endTime.setHours(23, 59, 59, 999); // End of reference day (or today)

  const startTime = referenceDate ? new Date(referenceDate) : new Date();
  startTime.setDate(startTime.getDate() - days);
  startTime.setHours(0, 0, 0, 0); // Start of that day

  return { startTime, endTime };
}

/**
 * Get the month-to-date range (from 1st of current month to reference date)
 * @param referenceDate Optional reference date (defaults to today)
 * @returns Object with startTime and endTime
 */
export function getMonthToDate(referenceDate?: Date): { startTime: Date; endTime: Date } {
  const endTime = referenceDate ? new Date(referenceDate) : new Date();
  endTime.setHours(23, 59, 59, 999); // End of reference day (or today)

  const startTime = referenceDate ? new Date(referenceDate) : new Date();
  startTime.setDate(1); // 1st of current month
  startTime.setHours(0, 0, 0, 0);

  return { startTime, endTime };
}

/**
 * Get all predefined time range presets for prefetching
 * These presets are calculated dynamically based on the current date
 * Ordered by priority: 30 days (default), 3 months, 7 days, MTD, 1 day
 * @returns Array of time range preset configurations
 */
export function getTimeRangePresets(): TimeRangePreset[] {
  const oneDayRange = getLastNDays(1);
  const sevenDaysRange = getLastNDays(7);
  const mtdRange = getMonthToDate();
  const thirtyDaysRange = getLastNDays(30);
  const threeMonthsRange = getLastNDays(90);

  return [
    // Priority 1: Default time range (30 days)
    {
      key: '30days-daily',
      label: '30 Days (Daily)',
      startTime: thirtyDaysRange.startTime,
      endTime: thirtyDaysRange.endTime,
      granularity: Granularity.DAY,
    },
    // Priority 2: 3 months (commonly used)
    {
      key: '3months-daily',
      label: '3 Months (Daily)',
      startTime: threeMonthsRange.startTime,
      endTime: threeMonthsRange.endTime,
      granularity: Granularity.DAY,
    },
    // Priority 3: 7 days (recent data)
    {
      key: '7days-daily',
      label: '7 Days (Daily)',
      startTime: sevenDaysRange.startTime,
      endTime: sevenDaysRange.endTime,
      granularity: Granularity.DAY,
    },
    // Priority 4: MTD (current month)
    {
      key: 'mtd-daily',
      label: 'MTD (Daily)',
      startTime: mtdRange.startTime,
      endTime: mtdRange.endTime,
      granularity: Granularity.DAY,
    },
    // Priority 5: 1 day (detailed view)
    {
      key: '1day-hourly',
      label: '1 Day (Hourly)',
      startTime: oneDayRange.startTime,
      endTime: oneDayRange.endTime,
      granularity: Granularity.HOUR,
    },
  ];
}

/**
 * Get the default time range (Last 30 days from today)
 * @returns Object with startTime and endTime
 */
export function getDefaultTimeRange(): { startTime: Date; endTime: Date } {
  return getLastNDays(30);
}

/**
 * Get the default granularity
 * @returns Default granularity (DAY)
 */
export function getDefaultGranularity(): Granularity {
  return Granularity.DAY;
}
